import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import { supabase } from '../../services/supabase';
import { Material } from '../../types/models';
import { applyAdminFilter, fixTypes } from '../../services/adminPermissions';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';

const MaterialsAdmin: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    category: '',
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });
  const [createCategory, setCreateCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      // Import the helpers we need
      const { fixTypes, getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      
      // Check if user is a superadmin
      const superAdmin = await isSuperAdmin();
      let queryResult;
      
      if (superAdmin) {
        // Superadmins see all materials
        queryResult = await supabase
          .from('materials')
          .select('*')
          .order('category')
          .order('created_at', { ascending: false });
      } else {
        // Regular admins might see only their club's materials
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          queryResult = { data: [], error: null };
        } else {
          try {
            // Try to filter by club_id if the column exists
            // First check if the materials table has a club_id column
            const testQuery = await supabase
              .from('materials')
              .select('id')
              .eq('club_id', userRole.club_id)
              .limit(1);
              
            // If no error, column exists, so filter by it
            queryResult = await supabase
              .from('materials')
              .select('*')
              .eq('club_id', userRole.club_id)
              .order('category')
              .order('created_at', { ascending: false });
          } catch (e) {
            // If club_id doesn't exist on this table, show all materials
            queryResult = await supabase
              .from('materials')
              .select('*')
              .order('category')
              .order('created_at', { ascending: false });
          }
        }
      }

      const { data, error } = queryResult;
      
      if (error) throw error;

      // Fix types to ensure proper typing
      const typedData = fixTypes<Material>(data || []);
      setMaterials(typedData);
      
      // Extract unique categories
      const uniqueCategories = Array.from(
        new Set(typedData.map(item => item.category) || [])
      ).sort();
      
      setCategories(uniqueCategories);
    } catch (err: any) {
      console.error('Error fetching materials:', err);
      showSnackbar(err.message || 'Failed to load materials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (material?: Material) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({
        name: material.name,
        link: material.link,
        category: material.category,
      });
    } else {
      setEditingMaterial(null);
      setFormData({
        name: '',
        link: '',
        category: categories.length > 0 ? categories[0] : '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCreateCategory(false);
    setNewCategory('');
  };

  // Handle text field changes
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };
  
  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSaveMaterial = async () => {
    try {
      // Validate form data
      if (!formData.name.trim() || !formData.link.trim() || (!formData.category.trim() && !newCategory.trim())) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      // Determine the category to use (existing or new)
      const categoryToUse = createCategory && newCategory.trim() ? newCategory.trim() : formData.category;

      // Get current user role to check for club_id
      const { getCurrentUserRole } = await import('../../services/adminPermissions');
      const userRole = await getCurrentUserRole();
      
      // Prepare the material data including club_id if applicable
      const materialData: any = {
        name: formData.name.trim(),
        link: formData.link.trim(),
        category: categoryToUse,
      };
      
      // If the user is a club admin, associate material with their club
      if (userRole?.club_id) {
        materialData.club_id = userRole.club_id;
      }

      if (editingMaterial) {
        // Update existing material
        const { error } = await supabase
          .from('materials')
          .update(materialData)
          .eq('id', editingMaterial.id);

        if (error) throw error;
        showSnackbar('Material updated successfully', 'success');
      } else {
        // Insert new material
        const { error } = await supabase
          .from('materials')
          .insert(materialData);

        if (error) throw error;
        showSnackbar('Material added successfully', 'success');
      }

      handleCloseDialog();
      fetchMaterials();
    } catch (err: any) {
      console.error('Error saving material:', err);
      showSnackbar(err.message || 'Failed to save material', 'error');
    }
  };

  const handleDeleteMaterial = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        // Get current user role to check permissions
        const { getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
        const userRole = await getCurrentUserRole();
        const superAdmin = await isSuperAdmin();
        
        // Build the delete query
        let query = supabase
          .from('materials')
          .delete();
        
        // Add appropriate filters
        // Only allow deleting if superadmin or if the material belongs to the admin's club
        if (!superAdmin && userRole?.club_id) {
          query = query.eq('club_id', userRole.club_id);
        }
        
        // Add the final ID filter
        const { error } = await query.eq('id', id);

        if (error) throw error;
        
        showSnackbar('Material deleted successfully', 'success');
        fetchMaterials();
      } catch (err: any) {
        console.error('Error deleting material:', err);
        showSnackbar(err.message || 'Failed to delete material', 'error');
      }
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Study Materials</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Material
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Link</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No materials found
                  </TableCell>
                </TableRow>
              ) : (
                materials.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell>{material.name}</TableCell>
                    <TableCell>{material.category}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: '250px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {material.link}
                        </Typography>
                        <IconButton
                          size="small"
                          href={material.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LaunchIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(material.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(material)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteMaterial(material.id)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Material Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMaterial ? 'Edit Material' : 'Add New Material'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Name"
            fullWidth
            value={formData.name}
            onChange={handleTextFieldChange}
            required
          />
          <TextField
            margin="dense"
            name="link"
            label="Link"
            fullWidth
            value={formData.link}
            onChange={handleTextFieldChange}
            required
            placeholder="https://"
          />

          {!createCategory ? (
            <Box mt={2} mb={1}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  label="Category"
                  onChange={handleSelectChange}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                onClick={() => setCreateCategory(true)}
                sx={{ mt: 1 }}
                size="small"
              >
                + Create New Category
              </Button>
            </Box>
          ) : (
            <Box mt={2}>
              <TextField
                margin="dense"
                name="newCategory"
                label="New Category"
                fullWidth
                value={newCategory}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCategory(e.target.value)}
                required
              />
              <Button 
                onClick={() => {
                  setCreateCategory(false);
                  setNewCategory('');
                }}
                sx={{ mt: 1 }}
                size="small"
              >
                Use Existing Category
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveMaterial} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaterialsAdmin;